// @flow

import _ from 'lodash';
import moment from 'moment';

import DateUtils from '../../utils/DateUtils';
import ArrayUtils from '../../utils/ArrayUtils';
import Utils from '../../utils/Utils';
import { getLocalizedData } from '../../utils/CatalogUtils';

import ApiObject from '../ApiObject';
import AvailabilityHours from './AvailabilityHours';

export default class CartModifier extends ApiObject {
  static ActionType = {
    DISCOUNT_PERCENTAGE: 'DISCOUNT_PERCENTAGE',
    DISCOUNT_AMOUNT: 'DISCOUNT_AMOUNT',
  };

  static TargetType = {
    SPECIFIC_ITEMS: 'SPECIFIC_ITEMS',
    // ALL_ITEMS_EXCEPT: 'ALL_ITEMS_EXCEPT',
    // SHIPPING_COST: 'SHIPPING_COST',
    TOTAL: 'TOTAL',
  };

  static TAG_LIMIT = 20;

  action: Object = {};

  id: string = null;

  restaurantId: string = null;

  name: string = '';

  localizedNames: Array<> = [];

  tag: string = '';

  localizedTags: Array<> = [];

  code: string = null;

  description: string = '';

  localizedDescriptions: Array<> = [];

  image: string = '';

  enabled: boolean = true;

  archived: boolean = false;

  combinable: boolean = false;

  condition: Object = {
    amountCondition: {
      includeShippingCostInMaxOrderAmount: false,
      includeShippingCostInMinOrderAmount: false,
      maxOrderAmount: 0,
      minOrderAmount: 0,
    },
    frontCondition: {
      apiConsumerIds: null,
      platforms: null,
    },
    orderModes: null,
    dateCondition: {
      beginningDate: null,
      endDate: null,
      weeklyAvailabilities: null,
    },
    itemCondition: null,
  };

  target: Object = {
    // TODO: remove after V1.
    // for now we only handle SPECIFIC_ITEMS
    type: CartModifier.TargetType.SPECIFIC_ITEMS,
    // type: null,
    items: null,
  };

  action: Object = {
    // TODO: remove default value after V1?
    type: CartModifier.ActionType.DISCOUNT_PERCENTAGE,
    // type: null,
    value: null,
  };

  set name(name) {
    this._name = name;
  }

  get name() {
    return getLocalizedData(this.localizedNames) || this._name || '';
  }

  set description(description) {
    this._description = description;
  }

  get description() {
    return getLocalizedData(this.localizedDescriptions) || this._description || '';
  }

  set tag(tag) {
    this._tag = tag;
  }

  get tag() {
    return getLocalizedData(this.localizedTags) || this._tag || '';
  }

  postJsonAssignation(data: Object) {
    super.postJsonAssignation(data);

    if (
      !_.isNil(data.condition) &&
      !_.isNil(data.condition.dateCondition) &&
      !_.isNil(data.condition.dateCondition.weeklyAvailabilities)
    ) {
      this.condition.dateCondition.weeklyAvailabilities = new AvailabilityHours().fromJson(
        data.condition.dateCondition.weeklyAvailabilities
      );
    }
  }

  getPrice(price: number) {
    return this.action.type === CartModifier.ActionType.DISCOUNT_PERCENTAGE
      ? parseFloat(((price * (100 - this.action.value)) / 100).toFixed(2))
      : parseFloat((price - this.action.value).toFixed(2));
  }

  // TODO: handle valid per actions type (percent, amount)
  // - hasValue (percent, amount)
  // - hasType
  // - has list of items
  isFormValid() {
    return this.validateName() && this.validateTag();
  }

  validateName(): boolean {
    if (_.isEmpty(this.localizedNames)) {
      return false;
    }

    let isValid = false;

    _.forEach(this.localizedNames, localizedName => {
      if (!_.isEmpty(localizedName.text)) {
        isValid = true;
      }
    });

    return isValid;
  }

  validateTag(): boolean {
    if (_.isEmpty(this.localizedTags)) {
      return false;
    }

    let isValid = false;

    _.forEach(this.localizedTags, localizedTag => {
      if (!_.isEmpty(localizedTag.text) && localizedTag.text.length <= CartModifier.TAG_LIMIT) {
        isValid = true;
      }
    });

    return isValid;
  }

  isAvailable(mode: number, dueDate: Moment) {
    return this.isAvailableForMode(mode) && this.isAvailableForDate(dueDate);
  }

  isAvailableForMode(mode: number) {
    return this.condition.orderModes === null || _.indexOf(this.condition.orderModes, mode) !== -1;
  }

  isAvailableForDate(date: Moment) {
    return (
      (_.isNil(this.condition.dateCondition.weeklyAvailabilities) ||
        this.condition.dateCondition.weeklyAvailabilities.isAvailableForDate(date)) &&
      (!this.hasDefinedDateCondition() ||
        this.isAvailableBetween(
          date,
          this.condition.dateCondition.beginningDate,
          this.condition.dateCondition.endDate
        ))
    );
  }

  hasDefinedDateCondition() {
    return (
      !_.isNull(this.condition.dateCondition.beginningDate) &&
      !_.isNull(this.condition.dateCondition.endDate)
    );
  }

  isAvailableBetween(date, beginningDate, endDate) {
    return (
      date.isAfter(DateUtils.fromMsTimestamp(beginningDate)) &&
      date.isBefore(DateUtils.fromMsTimestamp(endDate))
    );
  }

  hasDefinedWeeklyAvailabilities() {
    return !_.isNull(this.condition.dateCondition.weeklyAvailabilities);
  }

  hasDefinedComposition() {
    return (
      !_.isNull(this.condition.itemCondition) &&
      (!_.isNull(this.condition.itemCondition.itemsRequired) ||
        !_.isNull(this.condition.itemCondition.itemsForbidden))
    );
  }

  hasDefinedApiConsumerIds() {
    return (
      !_.isNull(this.condition.frontCondition) &&
      !_.isNull(this.condition.frontCondition.apiConsumerIds)
    );
  }

  hasDefinedTargetType() {
    return !_.isNull(this.target) && !_.isNull(this.target.type);
  }

  hasActionType(type) {
    return this.hasDefinedActionType() && this.action.type === type;
  }

  hasDefinedActionType() {
    return !_.isNil(this.action) && !_.isEmpty(this.action.type);
  }

  isFocusOnCheapestItems() {
    return !_.isNil(this.target.focusOnCheapestItems) && this.target.focusOnCheapestItems;
  }

  isOfferingShippingCost() {
    return this.target.type === CartModifier.TargetType.SHIPPING_COST;
  }

  getItemInformation(mode, dueDate) {
    return {
      id: this.id,
      tag: this.tag,
      description: this.description,
      type: this.action.type,
      priority: this.priority,
      value: this.action.value,
      combinable: this.combinable,
      modes: this.condition.orderModes,
      isHandled: !this.hasDefinedComposition(),
      isAvailable: this.isAvailable(mode, dueDate),
    };
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/menu/CartModifier.js